import { Fetcher, HTTPError, objectToFormData } from '@this/src/util';
/* eslint-disable max-lines */
import React from 'react';
import { Prompt } from 'react-router';
import _ from 'lodash';
import { withTheme } from 'styled-components';
import type { Moment } from 'moment';
import moment from 'moment';
import type { AITTheme } from '@this/components/shared/ui/theme';
import Text from '@this/components/shared/text/text';

import ContentHeader from '@this/components/shared/content_header/content_header';
import { Loading } from '@this/shared/ui/feedbacks/loading';

import User, { convertUserResponseToArgs } from '@this/domain/user/user2';
import type { UserResponseArgs } from '@this/domain/user/user2';
import type SelectRepository from '@this/domain/select_repository';
import type { PackageProvider, SelectType } from '@this/domain/select_store';
import type SelectStore from '@this/domain/select_store';
import type { AvailableRepository } from '@this/domain/available_repository';
import type { StaticContext } from 'react-router';
import type { RouteComponentProps } from 'react-router-dom';
import Trip, { convertTripResponseToArgs, type TripResponseArgs } from '@this/src/domain/trip/trip2';
import TravelerInformation, {
  buildTravelerParams,
  type TravelerInformationResponseArgs
} from '@this/domain/traveler_information';
import ReserveConfirmRoutingsTemplate from '@this/components/reserve_trip/reserve_confirm/routings/routings.template';
import type { ApproveItemsResponse, CardJson, ConsignmentSale } from '@this/domain/reserve_info';
import ReserveInfo from '@this/domain/reserve_info2';
import ReserveConfirmStore from '@this/src/domain/reserve_confirm_store';
import OrganizationBase, {
  convertOrganizationBaseResponseToArgs
} from '@this/domain/organization_base/organization_base2';
import type { OrganizationBaseResponseArgs } from '@this/domain/organization_base/organization_base2';
import Department, {
  convertDepartmentResponseToArgs,
  type DepartmentResponseArgs
} from '@this/domain/department/department2';
import Project, { convertProjectResponseToArgs, type ProjectResponseArgs } from '@this/domain/project/project2';
import type Prefecture from '@this/domain/prefecture';
import type { TripDuplicationResponseArgs } from '@this/domain/trip_duplication';
import MarginType, { convertMarginTypeResponseToArgs } from '@this/domain/organization/margin_type3';
import type { MarginTypeResponseArgs } from '@this/domain/organization/margin_type3';
import type { RentalCarLimitTypeJson } from '@this/domain/organization/rental_car_limit_type';
import RentalCarLimitType from '@this/domain/organization/rental_car_limit_type';
import ReservingTrip from '@this/domain/trip/reserving_trip2';
import ReservingTripItem, { convertReservingTripItemResponseToArgs } from '@this/domain/trip/reserving_trip_item2';
import type { OrderItemMappingArgs } from '@this/domain/order_item_mapping';
import type { WorkflowStyle } from '@this/domain/workflow_style';
import NonOrderItem2 from '@this/domain/non_order_item2';
import NonOrderItemPrice from '@this/domain/non_order_item_price2';
import type NonOrderItemMapping from '@this/domain/non_order_item_mapping2';
import type HotelElement from '@this/domain/hotel_element2';
import TransportElement from '@this/domain/transport_element2';
import TransportElementTerminal from '@this/domain/transport_element_terminal/transport_element_terminal2';
import ApproveItem, { convertApproveItemResponseToArgs } from '@this/domain/approve_item/approve_item2';
import { reportError } from '@this/lib/bugsnag';
import OrganizationError from '@this/components/trips_management/trips/organization_error';
import RentalCar, { convertRentalCarResponseToArgs, type RentalCarResponseArgs } from '@this/domain/rental_car';
import type { FlightResponseArgs } from '@this/domain/flight/flight2';
import type { HotelResponseArgs } from '@this/domain/hotel/hotel2';
import type { TransitResponseArgs } from '@this/domain/transit/transit2';
import TripsFormBasicinfoTemplate from './trips_form_block/trips_form_basicinfo.template';
import { Wrap, BodyWrap, Body, LoadingWrap } from '../trips/trips';

interface Props extends RouteComponentProps<Record<string, string | undefined>, StaticContext, unknown> {
  serviceId: number;
  availableRepos: AvailableRepository[];
  theme: AITTheme;
}

interface TripsState {
  serviceId: number;
  trip: Trip | null;
  user: User | null;
  members: User[];
  travelers: TravelerInformation[];
  travelerBirthdayInputs: string[];
  title: string;
  loading: boolean;
  shouldBlockNavigation: boolean;
  validationErrors: TripFormErrors;
  errorsInputCustomer: { [key: string]: string | undefined };
  outdate: Moment;
  homedate: Moment;
  price: number;
  boardingStation: string;
  arrivalStation: string;
  tripType: string;
  query: Record<string, unknown> | null;
  store: SelectStore | undefined;
  repository: SelectRepository | undefined;
  isConfirmingAddTraveler: boolean;
  removingTravelerNumber: number | null;
  error: string | null;
  reserveInfo: ReserveInfo | null;
  reserveConfirmStore: ReserveConfirmStore | null;
  searchQueryId: number | null;
  organizationBases: OrganizationBase[] | undefined;
  departments: Department[] | undefined;
  useBulkTicket: boolean | undefined;
  useKyuusyuuTicket: boolean | undefined;
  showEx: boolean | undefined;
  // loading: boolean;
  paymentLoading: boolean;
  applyType: string;
  termsAgree: boolean;
  projectShareAvailability: boolean;
  prefecture: Prefecture[];
  tripDuplications: TripDuplicationResponseArgs[];
  organizationError: boolean;
  isFreeeAuthenticated: boolean;
}

interface TripsResponse {
  trip: TripResponseArgs | null;
  user: UserResponseArgs;
  members: UserResponseArgs[];
  query: Record<string, unknown> | null;
  items: {
    element_id: string;
    element_raw: TransitResponseArgs | HotelResponseArgs | FlightResponseArgs;
    element_type: 'transport' | 'hotel' | 'flight';
    foreign_exist: string; // "false" で返ってくる;
    changeable_air: string; // "true" で返ってくる;
    index: string; // "0" で返ってくる
    domestic_air_price_index: number;
  }[];
  approvers: (UserResponseArgs & { approve_stage?: number })[];
  type?: SelectType;
  package_type: string | null;
  package_provider?: PackageProvider;
  package_search_condition: Record<string, unknown> | null;
  foreign_exist: boolean | string;
  domestic_exist: boolean | string;
  shinkansen_address: string | null;
  shinkansen_addressee: string | null;
  shinkansen_address_type: string | null;
  shinkansen_fee?: string | null;
  domestic_air_fee: string | null;
  foreign_air_fee: string | null;
  use_bulk_ticket: boolean;
  use_kyuusyuu_ticket: boolean;
  show_ex: boolean;
  organization_bases: OrganizationBaseResponseArgs[];
  departments: DepartmentResponseArgs[];
  charging_department_shares: Record<string, unknown> | null;
  postcode: string | null;
  cards: CardJson[];
  car_type: [string, string][];
  rentalcar_available: boolean;
  rental_cars: RentalCarResponseArgs[];
  rental_car_limit_type: RentalCarLimitTypeJson;
  is_smoke: [string, string][];
  projects: ProjectResponseArgs[] | null;
  project_shares: Record<string, unknown> | null;
  order_item_mapping_args: OrderItemMappingArgs[];
  outword: string | null;
  homeword: string | null;
  hotel?: { bed_types: { id: number; description: string }[] };
  flight: Record<string, unknown> | null;
  margin_types: MarginTypeResponseArgs[];
  show_fee: boolean;
  draft_trip: { purpose: string | null } | null;
  all_travelers_notification: boolean;
  notified_users: UserResponseArgs[];
  travelers: TravelerInformationResponseArgs[];
  traveler_candidates: UserResponseArgs[];
  consignment_sale: ConsignmentSale;
  login_user_id: number;
  internal_number: string;
  nationalities: { id: number; name: string }[];
  expenses_account_type_available: boolean;
  workflow_style: WorkflowStyle;
  use_smart_search: boolean;
  trip_duplications: TripDuplicationResponseArgs[];
  trip_approve_item: { json: string };
  organization_error: boolean;
  use_default_workflow: boolean;
}

interface TravelerErrors {
  [key: string]: string | undefined;
  memberSelect?: string | undefined;
  travelerFirstNameRomen?: string | undefined;
  travelerLastNameRomen?: string | undefined;
  travelerFirstNameKana?: string | undefined;
  travelerLastNameKana?: string | undefined;
  travelerFirstName?: string | undefined;
  travelerBirthday?: string | undefined;
  travelerGender?: string | undefined;
}

interface TripFormErrors {
  travelers: TravelerErrors[];
  price: string | null;
  errors: string[];
  [key: string]: unknown;
}

interface EkispertCourse {
  price: number;
  route: string;
}

interface EkispertPriceResponse {
  courses: EkispertCourse[];
}

interface FreeeAuthUriResponse {
  freee_auth_uri?: string | null;
}

const getNonOrderItemParams = (item: NonOrderItem2) => item.updateParams;

const logSimpleRequestState = (
  label: string,
  state: TripsState,
  pathname: string,
  extra?: Record<string, unknown>
) => {
  const isSimpleRequest = state.applyType === 'simple_request' || pathname.includes('/trips/simple_request');
  const foreignExist = state.reserveInfo?.foreignExist;
  const isForeign = Boolean(foreignExist) || isSimpleRequest;
  // eslint-disable-next-line no-console -- 既存調査用ログのため残す
  console.info('[TripsSimpleRequestForm]', label, {
    applyType: state.applyType,
    pathname,
    foreignExist,
    isSimpleRequest,
    isForeign,
    reservingTripIsForeign: state.reserveInfo?.reservingTrip?.isForeignFlightCustomerInfoRequired,
    ...extra
  });
};

const buildSimpleRequestNonOrderItem = (
  tripType: string,
  peopleNum: number,
  outdate: Moment,
  homedate: Moment,
  price: number,
  boardingStation: string,
  arrivalStation: string
) => {
  const timeZone = 'Asia/Tokyo';
  const from = new TransportElementTerminal({
    time: outdate.clone().startOf('day'),
    timeZone,
    name: boardingStation || ''
  });
  const to = new TransportElementTerminal({
    time: homedate.clone().startOf('day'),
    timeZone,
    name: arrivalStation || ''
  });
  const transport = new TransportElement({
    transportType: 'shinkansen',
    from,
    to,
    boardingStation,
    arrivalStation
  });
  const nonOrderItemMappings: NonOrderItemMapping[] = [];
  const hotels: HotelElement[] = [];
  const nonOrderItemPrice = new NonOrderItemPrice({ price, details: [] });
  return new NonOrderItem2({
    price: nonOrderItemPrice,
    peopleNum,
    nonOrderItemMappings,
    transports: [transport],
    hotels,
    paymentType: 'cash',
    requestType: 'simple_request',
    isNeed: true,
    supplier: '',
    tripType,
    elementType: 'shinkansen'
  });
};

const convertTravelerToTravelerInformation = (
  traveler: TravelerInformation,
  loginUser: User,
  members: User[]
): TravelerInformation => {
  const info = new TravelerInformation({});
  const travelerType = traveler.type;
  const travelerUserId = traveler.userId || traveler.id;
  if (travelerType === 'companion') {
    info.type = 'companion';
    info.firstNameRoman = traveler.firstNameRoman;
    info.lastNameRoman = traveler.lastNameRoman;
    info.firstNameKana = traveler.firstNameKana;
    info.lastNameKana = traveler.lastNameKana;
    info.flightFirstNameRoman = traveler.flightFirstNameRoman;
    info.flightLastNameRoman = traveler.flightLastNameRoman;
    info.flightMiddleNameRoman = traveler.flightMiddleNameRoman;
    info.flightFirstNameKana = traveler.flightFirstNameKana;
    info.flightLastNameKana = traveler.flightLastNameKana;
    info.flightGender = traveler.flightGender;
    info.flightBirthday = traveler.flightBirthday ? moment(traveler.flightBirthday) : undefined;
    info.flightTel = traveler.flightTel;
    info.passportNumber = traveler.passportNumber;
    info.passportExpire = traveler.passportExpire;
    info.passportIssuedCountry = traveler.passportIssuedCountry;
    info.immigrationCardCreation = traveler.immigrationCardCreation;
    info.visaApplication = traveler.visaApplication;
    info.nationalityId = traveler.nationalityId;
    return info;
  }
  if (travelerType === 'self') {
    info.type = 'self';
    info.assignFromUser(loginUser);
    return info;
  }
  if (travelerType === 'member') {
    info.type = 'member';
    if (travelerUserId) {
      const member = members.find(m => m.id === travelerUserId);
      if (member) {
        info.assignFromUser(member);
        return info;
      }
      info.userId = travelerUserId;
      info.userName = traveler.name;
      info.name = traveler.name;
    }
    return info;
  }
  if (travelerUserId && travelerUserId === loginUser.id) {
    info.type = 'self';
    info.assignFromUser(loginUser);
    return info;
  }
  if (travelerUserId) {
    info.type = 'member';
    const member = members.find(m => m.id === travelerUserId);
    if (member) {
      info.assignFromUser(member);
      return info;
    }
    info.userId = travelerUserId;
    info.userName = traveler.name;
    info.name = traveler.name;
    return info;
  }
  info.type = 'self';
  info.assignFromUser(loginUser);
  return info;
};

const decorateUserForForm = (user: User) => {
  const target = user as User & {
    departmentName?: () => string;
    show_other_members?: boolean;
  };
  if (typeof target.departmentName !== 'function') {
    target.departmentName = () => (user.department ? user.department.name : '');
  }
  if (typeof target.show_other_members === 'undefined') {
    target.show_other_members = user.showOtherMembers || false;
  }
  return target;
};

class TripsSimpleRequestForm extends React.Component<Props, TripsState> {
  constructor(props: Props) {
    super(props);
    const lState = props.location.state;

    this.state = {
      serviceId: props.serviceId,
      trip: null,
      user: null,
      members: [],
      travelers: [],
      travelerBirthdayInputs: [],
      title: utils.dig(lState, 'title') || '',
      loading: true,
      shouldBlockNavigation: true,
      validationErrors: {
        travelers: [],
        price: null,
        errors: []
      },
      errorsInputCustomer: {},
      outdate: utils.dig(lState, 'outdate') ? moment(utils.dig(lState, 'outdate')) : moment().add(1, 'days'),
      homedate: utils.dig(lState, 'homedate') ? moment(utils.dig(lState, 'homedate')) : moment().add(2, 'days'),
      price: utils.dig(lState, 'price'),
      boardingStation: '',
      arrivalStation: '',
      tripType: 'round_trip',
      query: null,
      store: undefined,
      repository: undefined,
      isConfirmingAddTraveler: false,
      removingTravelerNumber: null,
      error: null,
      reserveInfo: null,
      reserveConfirmStore: null,
      searchQueryId: null,
      organizationBases: undefined,
      departments: undefined,
      useBulkTicket: undefined,
      useKyuusyuuTicket: undefined,
      showEx: undefined,
      paymentLoading: false,
      applyType: 'simple_request',
      termsAgree: true,
      projectShareAvailability: false,
      prefecture: [],
      tripDuplications: [],
      organizationError: false,
      isFreeeAuthenticated: false
    };
  }

  componentDidMount() {
    const lState = this.props.location.state;
    Fetcher.get<TripsResponse>(`${location.href}.json`, lState || {}).then(
      async result => {
        const trip = result.trip
          ? new Trip(
              convertTripResponseToArgs(
                {
                  ...result.trip,
                  user: result.user
                },
                result.show_fee
              )
            )
          : null;
        const approvers = _.sortBy(result.approvers, 'approve_stage');
        if (!_.isUndefined(result.user.department)) {
          result.user.department.approvers = approvers;
        }
        const user = decorateUserForForm(new User(convertUserResponseToArgs(result.user)));
        const members = _.map(result.members, m => new User(convertUserResponseToArgs(m)));
        let travelers: TravelerInformation[];
        if (trip) {
          const tripTravelers = trip.travelerInformations || [];
          if (tripTravelers.length === 0) {
            const initial = new TravelerInformation({});
            initial.type = 'self';
            initial.assignFromUser(user);
            travelers = [initial];
          } else {
            travelers = tripTravelers.map(traveler =>
              convertTravelerToTravelerInformation(traveler, user, members)
            );
          }
        } else {
          const initial = new TravelerInformation({});
          initial.type = 'self';
          initial.assignFromUser(user);
          travelers = [initial];
        }
        if ((!trip || trip.id === null) && travelers[0]) {
          const selfTraveler = new TravelerInformation({ type: 'self' });
          selfTraveler.assignFromUser(user);
          travelers[0] = selfTraveler;
        }
        const outdate = utils.dig(lState, 'outdate') || !trip?.startTime ? this.state.outdate : trip.startTime;
        const homedate = utils.dig(lState, 'homedate') || !trip?.endTime ? this.state.homedate : trip.endTime;

        this.setState({
          trip,
          user,
          members,
          travelers,
          travelerBirthdayInputs: travelers.map(t => (t.birthday ? moment(t.birthday).format('YYYY-MM-DD') : '')),
          title: trip !== null && trip.id !== null ? trip.title || '' : this.state.title,
          outdate,
          homedate,
          price: this.state.price,
          query: null
        });

        const marginTypes = result.margin_types.map(raw => new MarginType(convertMarginTypeResponseToArgs(raw)));
        const rentalCarLimitType = new RentalCarLimitType(result.rental_car_limit_type);
        const shinkansenAddressType = result.shinkansen_address_type;
        const normalizedShinkansenAddressType =
          shinkansenAddressType === 'select' ||
          shinkansenAddressType === 'text' ||
          shinkansenAddressType === 'organization_base_select'
            ? shinkansenAddressType
            : undefined;
        const tripApproveItemJson = result.trip?.trip_approve_item?.json;
        const parsedTripApproveItems = tripApproveItemJson
          ? (JSON.parse(tripApproveItemJson) as { id: number; value: string; valueCode: string }[])
          : null;
        const reserveInfo = new ReserveInfo({
          reservingTrip: new ReservingTrip({
            packageType: result.type,
            peopleNum: travelers.length,
            travelers,
            rentalCars: result.rental_cars.map(raw => new RentalCar(convertRentalCarResponseToArgs(raw))),
            showFee: result.show_fee,
            marginTypes,
            carTypeOptions: result.car_type,
            items: _.map(
              result.items,
              item =>
                new ReservingTripItem(
                  convertReservingTripItemResponseToArgs({
                    ...item,
                    index: Number(item.index),
                    foreign_exist: item.foreign_exist === 'true'
                  })
                )
            )
          }),
          roomNum: 0,
          type: result.type,
          isSimpleRequest: this.state.applyType === 'simple_request',
          // travelers,
          user,
          foreignExist: result.foreign_exist === true || result.foreign_exist === 'true',
          shinkansenAddress: result.shinkansen_address ?? undefined,
          shinkansenAddressee: result.shinkansen_addressee ?? undefined,
          shinkansenAddressType: normalizedShinkansenAddressType,
          postcode: result.postcode ?? undefined,
          // carType: result.car_type,
          rentalCarAvailable: result.rentalcar_available,
          // rentalCars: result.rental_cars,
          // paymentMethodOptions: result.user.organization.payment_method_type,
          paymentGatewayMembers: [],
          paymentGatewayType: result.user.organization?.payment_gateway_type ?? undefined,
          paymentTransaction: { uid: 'new' },
          allTravelersNotification: result.all_travelers_notification,
          loginUserId: result.login_user_id,
          rentalCarLimitType,
          nationalities: result.nationalities,
          expensesAccountTypeAvailable: result.expenses_account_type_available,
          departments: (result.departments || []).map(d => new Department(convertDepartmentResponseToArgs(d))),
          projects: (result.projects || []).map(p => new Project(convertProjectResponseToArgs(p))),
          workflowStyle: result.workflow_style,
          useDefaultWorkflow: result.use_default_workflow
        });
        reserveInfo.finalDestination = result.trip?.final_destination ?? '';
        reserveInfo.purpose = result.trip?.purpose ?? '';
        reserveInfo.notifiedUsers = result.notified_users.map(
          user => new User(convertUserResponseToArgs(user as unknown as UserResponseArgs))
        );
        reserveInfo.internalNumber = result.internal_number ?? '';
        reserveInfo.approveItemValues = new Map<number, string>(
          parsedTripApproveItems?.map(item => [item.id, item.value]) ?? []
        );
        reserveInfo.approveItemValueCodes = new Map<number, string>(
          parsedTripApproveItems?.map(item => [item.id, item.valueCode]) ?? []
        );

        // 申請項目を取得
        const approveItemsResponse = await Fetcher.get<ApproveItemsResponse>(
          '/organization/approve_items/display.json',
          { target: 'simple_request_trip' }
        );
        reserveInfo.approveItems = approveItemsResponse.approve_items.map(
          item => new ApproveItem(convertApproveItemResponseToArgs(item))
        );
        logSimpleRequestState('after reserveInfo build', this.state, this.props.location.pathname, {
          apiForeignExist: result.foreign_exist
        });
        const reserveConfirmStore = new ReserveConfirmStore({
          reserveInfo,
          organizationBases: result.organization_bases.map(
            o => new OrganizationBase(convertOrganizationBaseResponseToArgs(o))
          ),
          showEx: result.show_ex,
          useBulkTicket: result.use_bulk_ticket,
          useKyuusyuuTicket: result.use_kyuusyuu_ticket,
          fromTripForm: true
        });
        this.setState(
          {
            reserveInfo,
            reserveConfirmStore,
            trip,
            user,
            travelers,
            departments: result.departments.map(d => new Department(convertDepartmentResponseToArgs(d))),
            organizationBases: result.organization_bases.map(
              o => new OrganizationBase(convertOrganizationBaseResponseToArgs(o))
            ),
            useBulkTicket: result.use_bulk_ticket,
            useKyuusyuuTicket: result.use_kyuusyuu_ticket,
            showEx: result.show_ex,
            loading: false,
            tripDuplications: result.trip_duplications,
            organizationError: result.organization_error
          },
          () => {
            logSimpleRequestState('after setState', this.state, this.props.location.pathname);
          }
        );
      },
      _error => {
        this.setState({
          error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        });
      }
    );
    this.fetchFreeeAuthUri();
  }

  fetchFreeeAuthUri() {
    Fetcher.get<FreeeAuthUriResponse>('/organization/freee/auth_uri.json').then(response => {
      if (response.freee_auth_uri) {
        this.setState({ isFreeeAuthenticated: false });
      } else {
        this.setState({ isFreeeAuthenticated: true });
      }
    });
  }

  componentDidUpdate() {
    // 未保存離脱の警告をPromptで実装しているが、再読み込みとタブの終了に対応していないため
    if (this.state.shouldBlockNavigation) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  }

  handleChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ title: e.target.value });
  };

  handleChangePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ price: parseInt(e.target.value, 10) });
  };

  handleTravelerTypeChange = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const travelerType = value as 'self' | 'member' | 'companion';
    this.setState(
      prev => {
        const travelers = [...prev.travelers];
        const travelerBirthdayInputs = [...prev.travelerBirthdayInputs];
        if (value === 'self') {
          const selfTraveler = new TravelerInformation({ type: 'self' });
          if (prev.user) {
            selfTraveler.assignFromUser(prev.user);
          }
          travelers[i] = selfTraveler;
          travelerBirthdayInputs[i] = '';
        } else if (travelerType === 'companion') {
          travelers[i] = new TravelerInformation({ type: 'companion' });
          travelerBirthdayInputs[i] = '';
        } else if (travelerType === 'member') {
          travelers[i] = new TravelerInformation({ type: 'member' });
          travelerBirthdayInputs[i] = '';
        } else {
          const traveler = travelers[i] || new TravelerInformation({});
          traveler.type = travelerType;
          travelers[i] = traveler;
        }
        return { travelers, travelerBirthdayInputs };
      },
      () => {}
    );
  };

  handleTravelerSelect = (i: number, traveler: TravelerInformation) => {
    this.setState(
      prev => {
        const travelers = [...prev.travelers];
        travelers[i] = traveler;
        return { travelers };
      },
      () => {}
    );
  };

  handleTravelerInfoChange = (i: number, method: string, e: React.ChangeEvent<HTMLInputElement> | null) => {
    const value = e?.target?.value ?? '';
    this.setState(
      prev => {
        const travelers = [...prev.travelers];
        const travelerBirthdayInputs = [...prev.travelerBirthdayInputs];
        const traveler = travelers[i];
        if (!traveler) {
          return prev;
        }
        switch (method) {
          case 'setLastNameRoman':
            traveler.lastNameRoman = value;
            break;
          case 'setFirstNameRoman':
            traveler.firstNameRoman = value;
            break;
          case 'setLastNameKana':
            traveler.lastNameKana = value;
            break;
          case 'setFirstNameKana':
            traveler.firstNameKana = value;
            break;
          case 'setBirthday':
            travelerBirthdayInputs[i] = value;
            if (moment(value, 'YYYY-MM-DD', true).isValid()) {
              traveler.birthday = moment(value, 'YYYY-MM-DD');
            } else {
              traveler.birthday = undefined;
            }
            break;
          case 'setFlightGender':
            traveler.flightGender = value as 'male' | 'female';
            break;
          default:
            break;
        }
        travelers[i] = traveler;
        return { ...prev, travelers, travelerBirthdayInputs };
      },
      () => {}
    );
  };

  handleChangeRoute = (name: string, value: string, _address: string) => {
    if (name === 'boardingStation') {
      this.setState({ boardingStation: value }, this.handleFetchPrice);
    } else {
      this.setState({ arrivalStation: value }, this.handleFetchPrice);
    }
  };

  handleChangeTripType = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ tripType: e.target.value }, this.handleFetchPrice);
  };

  handleFetchPrice = async () => {
    if (this.state.boardingStation && this.state.arrivalStation && this.state.outdate) {
      const params = {
        from: this.state.boardingStation,
        to: this.state.arrivalStation,
        date: this.state.outdate.format('YYYY/MM/DD'),
        search_for_jr_ex: true,
        trip_type: this.state.tripType
      };

      const q = utils.toParams(params);
      this.setState({ price: 0 });
      const res = await Fetcher.get<EkispertPriceResponse>(`/aiexpense/ekispert/price?${q}`);
      const course = res.courses[0];
      if (course) {
        this.setState({ price: course.price });
      }
    }
  };

  handleAddTraveler = (e?: React.MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
    }

    if (utils.dig(this.state.store, 'result', 'items') && !this.state.isConfirmingAddTraveler) {
      this.setState({
        isConfirmingAddTraveler: true
      });
    } else {
      if (!this.state.user) {
        return;
      }
      const traveler = new TravelerInformation({ type: 'self' });
      traveler.assignFromUser(this.state.user);
      this.setState(prev => ({
        travelers: [...prev.travelers, traveler],
        travelerBirthdayInputs: [...prev.travelerBirthdayInputs, '']
      }));
      this.setState({
        isConfirmingAddTraveler: false
      });
    }
  };

  handleRemoveTraveler = (i?: number, e?: React.MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
    }

    if (utils.dig(this.state.store, 'result', 'items') && i && !this.state.removingTravelerNumber) {
      this.setState({
        removingTravelerNumber: i
      });
    } else {
      const removingIndex = i ?? this.state.removingTravelerNumber;
      if (removingIndex === null || removingIndex === undefined) {
        return;
      }
      this.setState(prev => ({
        travelers: prev.travelers.filter((_traveler: TravelerInformation, idx: number) => idx !== removingIndex),
        travelerBirthdayInputs: prev.travelerBirthdayInputs.filter((_value, idx) => idx !== removingIndex)
      }));
      this.setState({
        removingTravelerNumber: null
      });
    }
  };

  handleChangeDate = (kind: 'outdate' | 'homedate', date: Moment) => {
    let outMoment = this.state.outdate;
    let homeMoment = this.state.homedate;

    switch (kind) {
      case 'outdate': {
        outMoment = date;
        if (date > homeMoment) {
          homeMoment = moment(outMoment);
        }
        break;
      }
      case 'homedate': {
        homeMoment = date;
        if (date < outMoment) {
          outMoment = moment(homeMoment);
        }
        break;
      }
      default: {
        break;
      }
    }
    this.setState(
      {
        outdate: outMoment,
        homedate: homeMoment
      },
      this.handleFetchPrice
    );
  };

  handleSubmit = async (e: React.MouseEvent<HTMLElement>) => {
    e.persist();
    this.setState({
      validationErrors: {
        travelers: [],
        price: null,
        errors: []
      },
      errorsInputCustomer: {}
    });

    this.syncTravelersToReserveInfo();

    // TripsFormBasicinfoTemplate の入力チェック
    const validationErrors = this.validationErrors();
    // ReserveConfirmRoutingsTemplate の入力チェック
    const reserveInfo = this.state.reserveInfo;
    reserveInfo?.setShowValidationErrors(true);
    const errors = reserveInfo?.validationErrors || {};
    const isValid = Object.keys(errors).length === 0;

    if (this.state.organizationError && reserveInfo?.workflowStyle !== 'trip') {
      e.preventDefault();
      this.setState({ validationErrors });
      this.setState({ errorsInputCustomer: errors });
      return;
    }

    if (_.isEmpty(validationErrors) && isValid) {
      this.submit();
    } else {
      e.preventDefault();
      this.setState({ validationErrors });
      this.setState({ errorsInputCustomer: errors });
    }
  };

  buildTravelerInformationParams = () =>
    buildTravelerParams(this.state.travelers, this.state.reserveInfo?.foreignExist ?? false);

  getApplicant() {
    const applicant = _.first(this.state.travelers);
    if (applicant) {
      return applicant;
    }
    const selfTraveler = new TravelerInformation({ type: 'self' });
    if (this.state.user) {
      selfTraveler.assignFromUser(this.state.user);
    }
    return selfTraveler;
  }

  syncTravelersToReserveInfo = () => {
    const { reserveInfo, travelers } = this.state;
    if (!reserveInfo?.reservingTrip) {
      return;
    }
    // 画面上の出張者（state.travelers）を reserve_info 送信データへ同期する。
    // 未同期のままだと「他の人の手配をする」選択がサーバーに反映されない。
    reserveInfo.reservingTrip.travelers = travelers;
    reserveInfo.reservingTrip.peopleNum = travelers.length;
  };

  addNonOrderItemToTrip = () => {
    if (!this.state.trip) return;
    const item = buildSimpleRequestNonOrderItem(
      this.state.tripType,
      this.state.travelers.length,
      this.state.outdate,
      this.state.homedate,
      this.state.price,
      this.state.boardingStation,
      this.state.arrivalStation
    );
    const trip = this.state.trip;
    const nonOrderItems = trip.nonOrderItems as unknown as NonOrderItem2[];
    nonOrderItems.splice(0, nonOrderItems.length, item);
    this.setState({ trip });
  };

  submit() {
    if (!this.state.trip) {
      return;
    }
    this.syncTravelersToReserveInfo();
    this.addNonOrderItemToTrip();
    logSimpleRequestState('before submit', this.state, this.props.location.pathname, {
      travelersCount: this.state.travelers.length
    });
    this.setState({ loading: true });
    const params = {
      as_draft: false,
      apply_type: this.state.applyType,
      payment_type: utils.dig(this.state.reserveInfo, 'paymentType') || 'billing',
      reserve_info: this.state.reserveInfo ? this.state.reserveInfo.submitParams : null,
      user_id: this.state.trip.userId,
      travelers: this.buildTravelerInformationParams(),
      title: this.state.title,
      departure_time: this.state.outdate.format('YYYY-MM-DD HH:mm'),
      return_time: this.state.homedate.format('YYYY-MM-DD HH:mm'),
      applicant_id: utils.dig(this.getApplicant(), 'id'),
      search_query_id: this.state.searchQueryId,
      non_order_items: _.compact(_.map(this.state.trip.nonOrderItems, item => getNonOrderItemParams(item)))
    };

    let path = '/trips';
    let type: 'POST' | 'PUT' = 'POST';
    if (this.state.trip.id) {
      path += `/${this.state.trip.id}`;
      type = 'PUT';
    }

    Fetcher.request({ url: `${path}.json`, data: objectToFormData(params), method: type })
      .then(_result => {
        this.setState(
          {
            shouldBlockNavigation: false
          },
          () => {
            this.props.history.push('/trips');
          }
        );
      })
      .catch(e => {
        let errors;
        if (e instanceof HTTPError && e.response?.data.errors) {
          errors = e.response?.data.errors;
        } else {
          errors = '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
        }
        this.setState({
          error: errors,
          loading: false
        });
      });
  }

  // TripsFormBasicinfoTemplateの入力チェック
  validationErrors() {
    const isSimpleRequest =
      this.state.applyType === 'simple_request' || this.props.location.pathname.includes('/trips/simple_request');
    const isForeign = Boolean(this.state.reserveInfo?.foreignExist) || isSimpleRequest;
    logSimpleRequestState('validationErrors', this.state, this.props.location.pathname, {
      isSimpleRequest,
      isForeign
    });
    const errors: TripFormErrors = {
      travelers: this.state.travelers.map((t: TravelerInformation, index: number) => {
        const tError: TravelerErrors = {};
        if (utils.dig(this.state.user, 'organization')) {
          t.validate(tError, isForeign);
        }
        if (index === 0) {
          // eslint-disable-next-line no-console -- 既存調査用ログのため残す
          console.info('[TripsSimpleRequestForm] traveler[0] validate', {
            type: t.type,
            firstNameKana: t.firstNameKana,
            lastNameKana: t.lastNameKana,
            flightFirstNameKana: t.flightFirstNameKana,
            flightLastNameKana: t.flightLastNameKana,
            tError
          });
        }
        if (isSimpleRequest && tError.travelerFirstName) {
          delete tError.travelerFirstName;
        }
        return tError;
      }),
      price: TripsSimpleRequestForm.numberError(
        '金額',
        this.state.price === null ? null : String(this.state.price)
      ),
      errors: []
    };

    errors.travelers = _.map(errors.travelers, te => utils.compactObject(te));
    errors.travelers = utils.compactObject(errors.travelers);
    errors.price = TripsSimpleRequestForm.numberError(
      '金額',
      this.state.price === null ? null : String(this.state.price)
    );

    return utils.compactObject(errors);
  }

  static requiredError(name: string, value: string | null, message = 'を入力してください') {
    if (!value || value === null || value.length === 0) {
      return name + message;
    }
    return undefined;
  }

  static katakanaError(name: string, value: string) {
    const error = TripsSimpleRequestForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!value.match(/^[ァ-ヶー　]*$/)) {
      return `${name}はカタカナで入力してください`;
    }
    return undefined;
  }

  static alphabetError(name: string, value: string) {
    const error = TripsSimpleRequestForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!value.match(/^[a-zA-Z]+$/)) {
      return `${name}はアルファベッドで入力してください`;
    }
    return undefined;
  }

  static dateError(name: string, value: string) {
    const error = TripsSimpleRequestForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!moment(value).isValid()) {
      return `${name}を正しく入力してください（例：1986-01-01）`;
    }
    return undefined;
  }

  static numberError(name: string, value: string | null) {
    const error = TripsSimpleRequestForm.requiredError(name, value);
    if (error) return error;
    if (value && !/^\d+$/.test(value)) {
      return `${name}は数字で入力してください`;
    }
    return null;
  }

  isCustomerInfoRequired = () => !!this.state.user && !!this.state.user.organization;

  handleBackToStep1Click = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        this.props.history.push(`${location.pathname}#`);
        this.setState({ shouldBlockNavigation: true });
      }
    );
  };

  handleValidationSuccess = () => {
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        this.props.history.push(`${location.pathname}#step2`);
        this.setState({ shouldBlockNavigation: true });
      }
    );
    return utils.scrollToTop();
  };

  handleConfirm = () => {};

  handleAgreementTermsCheck = () => {
    this.setState({
      termsAgree: !this.state.termsAgree
    });
  };

  clickBackLink = () => {
    const tripId = this.state.trip?.id;
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        this.props.history.push({
          pathname: tripId ? `/trips/${tripId}/edit` : '/trips/simple_request',
          state: this.backState()
        });
      }
    );
  };

  backState() {
    const trip = this.state.trip;
    const isForeign = this.state.reserveInfo?.reservingTrip?.isForeignFlightCustomerInfoRequired ?? false;
    return {
      title: trip?.title ?? this.state.title,
      travelers: this.state.travelers.map((traveler: TravelerInformation) =>
        traveler.getTravelerParams(isForeign)
      ),
      outdate: (trip?.startTime ?? this.state.outdate).format('YYYY-MM-DD HH:mm'),
      homedate: (trip?.endTime ?? this.state.homedate).format('YYYY-MM-DD HH:mm'),
      purpose: utils.dig(this.state.reserveInfo, 'purpose'),
      final_destination: utils.dig(this.state.reserveInfo, 'finalDestination'),
      non_order_items: _.compact(_.map(trip?.nonOrderItems ?? [], item => getNonOrderItemParams(item)))
    };
  }

  render() {
    const {
      trip,
      user,
      members,
      travelers,
      travelerBirthdayInputs,
      title,
      loading,
      shouldBlockNavigation,
      validationErrors,
      outdate,
      homedate,
      price,
      boardingStation,
      arrivalStation,
      tripType,
      error,
      reserveInfo,
      reserveConfirmStore,
      applyType,
      organizationError,
      isFreeeAuthenticated
    } = this.state;
    const styles = getStyles(this.props.theme);

    try {
      return (
        <Wrap>
          <Prompt
            when={shouldBlockNavigation}
            message="保存されていないデータが失われます。本当によろしいですか？"
          />
          <BodyWrap style={styles.formBodyWrap} className="trips-simple-request-form__body-wrap">
            <div>
              <Body style={styles.formBody}>
                <ContentHeader
                  title={`旅程情報${location.pathname === '/trips/simple_request' ? '登録' : '編集'}`}
                  backButton
                  backLink="/trips"
                />
                <div style={styles.tripForm} className="trips-simple-request-form__trip-form">
                  <div className="trips-simple-request-form__trip-form-left">
                    {loading || !user ? (
                      <LoadingWrap />
                    ) : (
                      <TripsFormBasicinfoTemplate
                        editable={!!(trip === null || trip.id === null)}
                        travelers={travelers}
                        travelerBirthdayInputs={travelerBirthdayInputs}
                        user={user}
                        members={members}
                        outdate={outdate}
                        homedate={homedate}
                        title={title}
                        price={price}
                        boardingStation={boardingStation}
                        arrivalStation={arrivalStation}
                        tripType={tripType}
                        validationErrors={validationErrors}
                        handleChangeTitle={this.handleChangeTitle}
                        handleChangePrice={this.handleChangePrice}
                        handleRemoveTraveler={this.handleRemoveTraveler}
                        handleTravelerTypeChange={this.handleTravelerTypeChange}
                        handleTravelerSelect={this.handleTravelerSelect}
                        handleTravelerInfoChange={this.handleTravelerInfoChange}
                        handleAddTraveler={this.handleAddTraveler}
                        handleChangeDate={this.handleChangeDate}
                        handleChangeRoute={this.handleChangeRoute}
                        handleChangeTripType={this.handleChangeTripType}
                        isSimpleRequest={applyType === 'simple_request'}
                      />
                    )}
                  </div>
                  <div className="trips-simple-request-form__trip-form-right">
                    <div style={styles.section}>
                      <div style={styles.sectionIn}>
                        {(trip === null || trip.tripDetailEditable) &&
                          !(
                            user?.organization?.freeeWorkflow &&
                            reserveInfo?.isApprovalRequired &&
                            !isFreeeAuthenticated
                          ) && (
                            <button
                              type="button"
                              onClick={e => this.handleSubmit(e)}
                              disabled={loading}
                              style={styles.submitButton}
                              className="trips-simple-request-form__submit-button"
                            >
                              申請する
                            </button>
                          )}
                        {!_.isEmpty(validationErrors.errors) && (
                          <div style={styles.error}>
                            <Text text={validationErrors.errors.join('\n')} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {loading ? (
                  <LoadingWrap>
                    <Loading />
                  </LoadingWrap>
                ) : (
                  <div>
                    {reserveConfirmStore && shouldBlockNavigation && (
                      // 上のshouldBlockNavigationはステップを行き来するときに一時非表示にすることで中身の更新を促すため
                      <ReserveConfirmRoutingsTemplate
                        store={reserveConfirmStore}
                        isSimpleRequest={applyType === 'simple_request'}
                        handleBackToSelectClick={this.clickBackLink}
                        handlePaymentSubmit={this.handleConfirm}
                        isOnTripsConfirm
                      />
                    )}
                    {organizationError && reserveInfo?.workflowStyle !== 'trip' && user && (
                      <div
                        style={styles.organizationErrorWrapper}
                        className="trips-simple-request-form__organization-error-wrapper"
                      >
                        <OrganizationError user={user} />
                      </div>
                    )}
                  </div>
                )}
              </Body>
            </div>
            <div style={styles.footerActionGroup}>
              <div style={styles.footerButtonArea}>
                {error && <div style={styles.error}>{error}</div>}
                {(trip === null || trip.tripDetailEditable) &&
                  !(
                    user?.organization?.freeeWorkflow &&
                    reserveInfo?.isApprovalRequired &&
                    !isFreeeAuthenticated
                  ) && (
                    <button
                      type="button"
                      onClick={e => this.handleSubmit(e)}
                      disabled={loading}
                      style={styles.submitButton}
                      className="trips-simple-request-form__submit-button"
                    >
                      申請する
                    </button>
                  )}
              </div>
            </div>
          </BodyWrap>
        </Wrap>
      );
    } catch (e) {
      reportError(e);
      // eslint-disable-next-line no-console -- 既存調査用ログのため残す
      console.error('[TripsSimpleRequestForm] render error', e);
      return null;
    }
  }
}

const getStyles = (theme: AITTheme) => ({
  formBodyWrap: {
    padding: 0
  } as React.CSSProperties,
  formBody: {
    padding: 20
  } as React.CSSProperties,
  tripForm: {
    padding: 10,
    marginBottom: 40,
    border: `solid 1px ${theme.grayBorderColor}`
  } as React.CSSProperties,
  section: {
    paddingBottom: 20
  } as React.CSSProperties,
  sectionIn: {
    marginLeft: 10,
    fontSize: 13
  } as React.CSSProperties,
  error: {
    color: theme.redColor
  } as React.CSSProperties,
  organizationErrorWrapper: {
    fontSize: 13
  } as React.CSSProperties,
  submitButton: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: theme.linkColor,
    color: '#fff',
    border: 'none',
    borderRadius: theme.buttonRadius,
    cursor: 'pointer',
    fontSize: 13,
    width: 200,
    textAlign: 'center' as const
  } as React.CSSProperties,
  footerActionGroup: {
    background: '#fff',
    position: 'sticky' as const,
    bottom: 0,
    boxShadow: '0 -3px 3px 3px rgba(0, 0, 0, 0.1)'
  } as React.CSSProperties,
  footerButtonArea: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: 20
  } as React.CSSProperties
});

export default withTheme(TripsSimpleRequestForm);
